'use client';

import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';

import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { InstantSearch, Configure } from 'react-instantsearch';
import classNames from 'classnames';
import {
  ALGOLIA_INDEX_NAME,
  NUMBER_OF_PRODUCTS_TO_SHOW,
} from 'commons/constants';
import scrollToTop from 'commons/scrollToTop';
import { useTransition } from './hooks/reactShims';
import AlgoliaSearchBox from './components/AlgoliaSearchBox';
import AlgoliaHits from './components/AlgoliaHits';
import AdvancedSearchContext from './hooks/AdvancedSearchContext';

import useAdvancedSearchURL from './hooks/useAdvancedSearchURL';
import MobileSearchIndexSelector from './components/MobileSearchIndexSelector';

const AdvancedSearchNext = () => {
  const {
    pathname,
    queryUrlParam,
    categoryUrlParam,
    updateUrlParam,
  } = useAdvancedSearchURL();

  const pendingQueryRef = useRef(null);
  const [remountCounter, setRemountCounter] = useState(0);
  const previousQueryRef = useRef(queryUrlParam);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousQueryRef.current = queryUrlParam;
      return;
    }

    const queryChanged = queryUrlParam !== previousQueryRef.current;
    const queryMatchesPending = queryUrlParam === pendingQueryRef.current;
    const wasExternalNavigation =
      queryChanged && !queryMatchesPending && queryUrlParam;

    if (wasExternalNavigation) {
      setRemountCounter(c => c + 1);
    }

    if (queryMatchesPending) {
      pendingQueryRef.current = null;
    }

    previousQueryRef.current = queryUrlParam;
  }, [queryUrlParam]);

  const onQueryChanging = useCallback(newQuery => {
    pendingQueryRef.current = newQuery;
  }, []);

  const instantSearchKey = useMemo(() => `${pathname}-${remountCounter}`, [
    pathname,
    remountCounter,
  ]);

  const [currentProductIndex, setCurrentProductIndex] = useState(
    ALGOLIA_INDEX_NAME.PRODUCTS
  );
  const [currentSearchSection, setCurrentSearchSection] = useState(
    categoryUrlParam ?? ALGOLIA_INDEX_NAME.PRODUCTS
  );

  const [isReadyToShipActive, setIsReadyToShipActive] = useState(false);
  const [minWidth, setMinWidth] = useState('');
  const [maxWidth, setMaxWidth] = useState('');

  const [selectedColors, setSelectedColors] = useState({});

  const [, startTransition] = useTransition();

  const updateSearchSection = useCallback(
    indexName => {
      startTransition(() => {
        setCurrentSearchSection(indexName);
        updateUrlParam({ category: indexName });
      });
      scrollToTop();
    },
    [setCurrentSearchSection, updateUrlParam]
  );

  const updateCurrentProductIndex = useCallback(
    indexName => {
      startTransition(() => {
        setCurrentProductIndex(indexName);
      });
    },
    [setCurrentProductIndex]
  );

  const handleToggleReadyToShip = useCallback(checked => {
    setIsReadyToShipActive(checked);
  }, []);

  const handleWidthChange = useCallback((min, max) => {
    setMinWidth(min);
    setMaxWidth(max);
  }, []);

  const selectColor = useCallback(
    colorLabel => {
      const isSelected = !!selectedColors?.[colorLabel];
      const newSelectedColors = {
        ...selectedColors,
        [colorLabel]: !isSelected,
      };
      setSelectedColors(newSelectedColors);
    },
    [selectedColors]
  );

  const context = useMemo(
    () => ({
      currentSearchSection,
      currentProductIndex,
      updateSearchSection,
      updateCurrentProductIndex,
      isReadyToShipActive,
      handleToggleReadyToShip,
      minWidth,
      maxWidth,
      handleWidthChange,
      selectedColors,
      selectColor,
    }),
    [
      currentSearchSection,
      currentProductIndex,
      updateSearchSection,
      updateCurrentProductIndex,
      isReadyToShipActive,
      handleToggleReadyToShip,
      minWidth,
      maxWidth,
      handleWidthChange,
      selectedColors,
      selectColor,
    ]
  );

  const initialUiState = useMemo(() => {
    const initialQuery = queryUrlParam || '';
    const uiState = {
      [ALGOLIA_INDEX_NAME.PRODUCTS]: {
        query: initialQuery,
      },
      [ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS]: {
        query: initialQuery,
      },
      [ALGOLIA_INDEX_NAME.REVIEWS]: {
        query: initialQuery,
      },
      [ALGOLIA_INDEX_NAME.STATIC_PAGES]: {
        query: initialQuery,
      },
    };
    return uiState;
  }, [queryUrlParam]);

  const facetFilters = useMemo(
    () => (isReadyToShipActive ? ['is_in_stock:true'] : []),
    [isReadyToShipActive]
  );

  // Align with Alexandria getProductsStaticData(showSearchProductsOnly): visibility 2 or 3, item_availability 0
  const numericFilters = useMemo(() => {
    const filters = ['item_availability=0', 'visibility>1', 'visibility<4'];

    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);

    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`width>=${minNum}`);
    }

    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`width<=${maxNum}`);
    }

    return filters;
  }, [minWidth, maxWidth]);

  return (
    <AdvancedSearchContext.Provider value={context}>
      <div
        className={classNames(
          'w-full mt-0 lg:mt-',
          '[&_.ais-SearchBox-input]:pl-[30px] [&_.ais-SearchBox-input]:indent:[unset] [&_.ais-SearchBox-input]:text-left',
          '[&_.ais-SearchBox-input::placeholder]:text-left [&_.ais-SearchBox-input::placeholder]:indent:[unset]'
        )}
      >
        <InstantSearch
          key={instantSearchKey}
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
          initialUiState={initialUiState}
          future={{ preserveSharedStateOnUnmount: true }}
        >
          <div className="relative h-auto lg:z-[1]">
            <AlgoliaSearchBox onQueryChanging={onQueryChanging} />
          </div>

          <div className="mt-0 mx-0 mb-0 lg:mb-8 lg:flex">
            <MobileAndTabletOnly>
              <MobileSearchIndexSelector />
            </MobileAndTabletOnly>
          </div>

          <Configure
            facets={['*']}
            facetFilters={facetFilters}
            numericFilters={numericFilters}
            hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW}
            attributesToRetrieve={[
              '_highlightResult',
              'product_id',
              'name',
              'is_in_stock',
            ]}
            clickAnalytics
          />

          <AlgoliaHits />
        </InstantSearch>
      </div>
    </AdvancedSearchContext.Provider>
  );
};

export default AdvancedSearchNext;
